import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Send, Plus, Mail, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { initials, timeAgo, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function Messages() {
  const { convId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [convos, setConvos] = useState(null);
  const [openConv, setOpenConv] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  const loadConvos = () => api.get("/pm/conversations").then((r) => setConvos(r.data.conversations));

  useEffect(() => { if (user) loadConvos(); }, [user]);
  useEffect(() => {
    if (!convId) { setOpenConv(null); return; }
    api.get(`/pm/conversations/${convId}`).then((r) => setOpenConv(r.data)).catch(() => nav("/messages"));
  }, [convId, nav]);

  if (!user) return null;

  return (
    <div className="space-y-4" data-testid="messages-page">
      <Breadcrumbs items={[{ label: t.home, to: "/" }, { label: "نجی پیغامات" }]} />
      <div className="flex items-center justify-between border-b-2 border-primary pb-3">
        <h1 className="urdu-naskh text-3xl font-semibold text-primary flex items-center gap-2">
          <Mail size={22} /> نجی پیغامات
        </h1>
        <NewConversationDialog onCreated={(id) => { loadConvos(); nav(`/messages/${id}`); }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-1 max-h-[70vh] overflow-y-auto divide-y divide-border" data-testid="convo-list">
          {convos === null && <Skeleton className="h-40" />}
          {convos && convos.length === 0 && (
            <div className="p-6 text-center urdu-naskh text-muted-foreground text-sm">— کوئی گفتگو نہیں</div>
          )}
          {convos && convos.map((c) => (
            <Link
              key={c.id}
              to={`/messages/${c.id}`}
              className={`block px-4 py-3 hover:bg-muted transition-colors ${c.id === convId ? "bg-primary/10" : ""}`}
              data-testid={`convo-${c.id}`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={c.other?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary font-eng text-xs">{initials(c.other?.username)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between">
                    <span className="font-eng font-semibold text-primary">{c.other?.username}</span>
                    <span className="text-[11px] text-muted-foreground font-eng">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <div className="text-xs urdu-naskh text-muted-foreground line-clamp-1">{c.last_message_snippet}</div>
                </div>
                {c.unread > 0 && (
                  <span className="min-w-[18px] h-5 px-1 rounded-full bg-accent text-accent-foreground text-[11px] font-eng font-bold flex items-center justify-center">
                    {c.unread}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </Card>

        <div className="md:col-span-2">
          {openConv ? <ConversationView data={openConv} onSent={() => api.get(`/pm/conversations/${convId}`).then((r) => setOpenConv(r.data))} /> : (
            <Card className="p-10 text-center urdu-naskh text-muted-foreground">
              گفتگو منتخب کریں یا نئی شروع کریں
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ConversationView({ data, onSent }) {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/pm/send", { conversation_id: data.conversation.id, content: msg });
      setMsg("");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "پیغام نہیں بھیج سکا");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="flex flex-col h-[70vh]" data-testid="conversation-view">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarImage src={data.other?.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary font-eng text-xs">{initials(data.other?.username)}</AvatarFallback>
        </Avatar>
        <div>
          <div className="font-eng font-semibold text-primary">{data.other?.username}</div>
          <div className="text-[11px] text-muted-foreground urdu-naskh">نجی گفتگو</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30" data-testid="messages-list">
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-md rounded-2xl px-4 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                <div className="urdu-naskh" dangerouslySetInnerHTML={{ __html: m.content }} />
                <div className={`text-[10px] font-eng mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{fmtDate(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 border-t border-border flex gap-2">
        <Textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          placeholder="پیغام لکھیے…"
          className="urdu-naskh resize-none"
          data-testid="pm-input"
        />
        <Button onClick={send} disabled={sending} className="bg-primary self-end gap-1" data-testid="pm-send">
          <Send size={14} /> بھیجیں
        </Button>
      </div>
    </Card>
  );
}

function NewConversationDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [uname, setUname] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!uname.trim() || !msg.trim()) return toast.error("صارف نام اور پیغام دونوں درکار ہیں");
    setBusy(true);
    try {
      const { data } = await api.post("/pm/start", { to_username: uname.trim(), content: msg });
      onCreated(data.conversation_id);
      setOpen(false);
      setUname("");
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "گفتگو شروع نہیں ہو سکی");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary gap-1" data-testid="new-pm-btn">
          <Plus size={14} /> <span className="urdu-naskh">نئی گفتگو</span>
        </Button>
      </DialogTrigger>
      <DialogContent dir="rtl">
        <DialogHeader>
          <DialogTitle className="urdu-naskh text-xl">نئی گفتگو شروع کریں</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label className="urdu-naskh text-sm">صارف کا نام</label>
          <Input value={uname} onChange={(e) => setUname(e.target.value)} dir="ltr" placeholder="username" data-testid="new-pm-username" />
          <label className="urdu-naskh text-sm">پہلا پیغام</label>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} className="urdu-naskh" data-testid="new-pm-message" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>منسوخ</Button>
          <Button onClick={start} disabled={busy} className="bg-primary" data-testid="new-pm-submit">
            {busy ? "بھیج رہا ہے…" : "شروع کریں"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
